/**
 * HUD over the twin scene: the asset's name (useAssetName: title and
 * secondary line) and overall status, the view mode
 * segmented control (1 to 4) with the explode slider, labels (L), the glow
 * (post-processing) switch, subviews, the signal path picker, the source
 * chip with staleness, the SIM interval and the live or sim switch, failure
 * simulation, alerts, snapshot, the dialog buttons, the event log strip and
 * the procedure strip. Also hosts the procedure runner and the keyboard
 * shortcuts (Escape order in useTwinKeys).
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useCatalogueView } from '@/features/catalogue/view';
import { useDive } from '@/features/dive/store';
import {
  Box,
  Camera,
  ClipboardList,
  Flame,
  Globe2,
  HeartPulse,
  Layers,
  Pause,
  Play,
  Radio,
  Scan,
  ScrollText,
  SkipBack,
  SkipForward,
  Sparkles,
  Square,
  Tag,
  type LucideIcon,
} from 'lucide-react';
import { Chip, IconButton, TextButton } from '@/components/ui/primitives';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { useUI } from '@/state/store';
import { getTwinHandle } from '../../scene/TwinHandle';
import { useTwin, type LiveSource } from '../../store';
import { isModelOnlyElement } from '../../data/modelParts';
import { useLiveFeed } from '../../telemetry/liveFeedStore';
import type { ViewMode } from '../../types';
import { isLiveSite, stalenessOf } from '../adapters/telemetry';
import { Freshness, StatusPill } from '../components/badges';
import { AlertsPopover } from '../dialogs/AlertsPanel';
import { EventLog } from '../dialogs/EventLog';
import { FailureSimMenu } from '../dialogs/FailureSimMenu';
import { useAssetName } from '../hooks/useAssetName';
import { useNowMs } from '../hooks/useNowMs';
import { useSubsystemColour } from '../hooks/useItemLookup';
import { dateStamp } from '@/features/loader/export/download';
import { colourLabel, worstColour } from '../lib/values';
import { useProcedureRunner } from '../procedures/useProcedureRunner';
import { useTwinUi } from '../state';
import { DiveReturn } from '@/features/dive/DiveReturn';
import { isOrbitalAsset } from '../../orbit/store';
import { SatelliteOrbitControls } from '../../orbit/OrbitControls';
import { SiteControls } from '../../site/SiteControls';
import { FacilityControls } from '../../site/FacilityControls';
import { siteProfile } from '../../site/profiles';
import { GroundStationControls } from './GroundStationControls';
import { AssetDescription } from './AssetDescription';
import { twinActionClass } from './styles';

const VIEW_MODES: Array<{ mode: ViewMode; label: string; key: string; Icon: LucideIcon }> = [
  { mode: 'solid', label: 'Solid', key: '1', Icon: Box },
  { mode: 'xray', label: 'X-ray', key: '2', Icon: Scan },
  { mode: 'thermal', label: 'Thermal', key: '3', Icon: Flame },
  { mode: 'exploded', label: 'Exploded', key: '4', Icon: Layers },
];

const SIM_INTERVALS = [2000, 5000, 10000, 15000, 30000, 60000];

const chrome =
  'pointer-events-auto rounded-panel border border-hairline-strong bg-raised/85 shadow-elev-1 backdrop-blur';
/** A control group of the HUD rows: never shrinks, never wraps inside, so a label never breaks over two lines. */
const group = 'flex shrink-0 items-center gap-1 whitespace-nowrap';

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return (
    !!t &&
    (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
  );
}

/**
 * True while a dialog or popover is open (Radix marks both role="dialog"):
 * Escape belongs to it. Hidden ones do not count.
 */
function dialogOpen(): boolean {
  for (const el of document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]')) {
    if (el.getAttribute('data-state') === 'closed') continue;
    if (typeof el.checkVisibility === 'function' && !el.checkVisibility()) continue;
    return true;
  }
  return false;
}

/**
 * 1 to 4 pick the view mode, L toggles labels. Escape, in this order: an open
 * dialog or popover keeps it (Radix closes it); a running procedure stops; an
 * open GAIA column closes; otherwise the key reaches the shell, whose
 * bubble-phase handler clears the selection. Typing in a field (the GAIA
 * composer included) leaves Escape to that field's own handler.
 */
function useTwinKeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e) || e.ctrlKey || e.metaKey || e.altKey) return;
      const twin = useTwin.getState();
      if (e.key === 'Escape') {
        if (dialogOpen()) return;
        if (twin.procedure) twin.stopProcedure();
        else if (useUI.getState().gaiaOpen) useUI.getState().setGaiaOpen(false);
        else return;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (!twin.element) return;
      const mode = VIEW_MODES.find((v) => v.key === e.key);
      if (mode) {
        twin.setViewMode(mode.mode);
        e.preventDefault();
        return;
      }
      if (e.key === 'l' || e.key === 'L') {
        twin.setLabelsVisible(!twin.labelsVisible);
        e.preventDefault();
      }
    };
    // Capture phase so Esc reaches the procedure and GAIA before the shell's bubble-phase handler clears the selection.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
}

/** "Receive chain" reads "Receive" on a narrow stage (the button keeps the full name for assistive tech). */
function shortPathLabel(label: string): string {
  return label.replace(/\s+chain$/i, '');
}

/**
 * Layout: the identity card and the data controls share the first row, the
 * scene toolbar is the row under them, both in normal flow inside one
 * container across the stage, so nothing overlaps whatever the stage width;
 * groups wrap as wholes when the stage is too narrow (GAIA and the detail
 * panel open on a small screen). Container queries (the HUD is the
 * container) drop the view-mode icons and key hints, tighten the button
 * padding and shorten the signal path names below 64rem of stage width.
 */
export function TwinHud() {
  const navigate = useNavigate();
  const diving = useDive((s) => s.phase !== 'idle');
  useTwinKeys();
  useProcedureRunner();
  const element = useTwin((s) => s.element);
  const asset = useTwin((s) => s.asset);
  const loadStatus = useTwin((s) => s.loadStatus);
  const loadError = useTwin((s) => s.loadError);
  const viewMode = useTwin((s) => s.viewMode);
  const setViewMode = useTwin((s) => s.setViewMode);
  const explode = useTwin((s) => s.explode);
  const setExplode = useTwin((s) => s.setExplode);
  const labelsVisible = useTwin((s) => s.labelsVisible);
  const setLabelsVisible = useTwin((s) => s.setLabelsVisible);
  const bloom = useTwin((s) => s.bloom);
  const setBloom = useTwin((s) => s.setBloom);
  const subviewId = useTwin((s) => s.subviewId);
  const setSubview = useTwin((s) => s.setSubview);
  const activeSignalPathId = useTwin((s) => s.activeSignalPathId);
  const setSignalPath = useTwin((s) => s.setSignalPath);
  const liveSource = useTwin((s) => s.liveSource);
  const setLiveSource = useTwin((s) => s.setLiveSource);
  const simIntervalMs = useTwin((s) => s.simIntervalMs);
  const setSimIntervalMs = useTwin((s) => s.setSimIntervalMs);
  const readings = useTwin((s) => s.readings);
  const procedure = useTwin((s) => s.procedure);
  const assetId = useUI((s) => s.selection.assetId);
  const openDialog = useTwinUi((s) => s.openDialog);
  const feedConnection = useLiveFeed((s) => s.connection);
  const feedSource = useLiveFeed((s) => s.source);
  const feedLastSeenMs = useLiveFeed((s) => s.lastSeenMs);
  const feedStale = useLiveFeed((s) => s.stale);
  const subsystemColour = useSubsystemColour();
  const name = useAssetName();
  const now = useNowMs(2000, isLiveSite(assetId));

  if (!assetId) return null;

  const overall = element?.subsystems.length
    ? worstColour(element.subsystems.map((s) => subsystemColour(s)))
    : null;
  const simStaleness = stalenessOf(readings, now);
  const liveCapable = isLiveSite(assetId);
  const referenceOnly = isModelOnlyElement(element);
  const measured = liveSource === 'live' && feedSource === 'influxdb';
  const sourceTone = measured
    ? feedStale
      ? 'caution'
      : 'live'
    : liveSource === 'sim' || feedSource === 'stub'
      ? 'sim'
      : 'neutral';
  const sourceLabel =
    liveSource === 'live'
      ? feedSource === 'stub'
        ? 'SIM · feed fallback'
        : measured
          ? 'LIVE · site sensors'
          : 'Connecting to feed'
      : liveSource === 'sim'
        ? 'SIM'
        : 'JSON';
  const freshMs = liveSource === 'live' ? 120_000 : simIntervalMs * 2;
  // Live: age of the newest Influx point for the site (stale past two minutes); SIM: age of the newest simulated reading.
  const ageMs =
    liveSource === 'live'
      ? feedLastSeenMs !== null
        ? Math.max(0, now - feedLastSeenMs)
        : null
      : simStaleness.ageMs;
  const feedNote = liveSource === 'live' ? (feedConnection === 'open' ? 'stream' : feedConnection) : null;
  const subviews = asset?.subviews ?? [];
  const signalPaths =
    asset?.subviews?.find((v) => v.id === subviewId)?.signalPaths ?? asset?.signalPaths ?? [];

  const snapshot = () => {
    const handle = getTwinHandle();
    if (!handle) {
      toast('warn', 'The scene is not ready for a snapshot.');
      return;
    }
    const url = handle.snapshot();
    const a = document.createElement('a');
    a.href = url;
    a.download = `${assetId}_${dateStamp()}.png`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="@container pointer-events-none absolute inset-0 z-20 text-xs" data-testid="twin-hud">
      <div className="absolute inset-x-3 top-3 flex flex-col items-start gap-2 @5xl:inset-x-4 @5xl:top-4">
        {/* Row 1: identity and status on the left, data source, simulation, alerts and dialogs on the right */}
        <div className="flex w-full flex-wrap items-start justify-between gap-2">
          <div
            className={cn(
              chrome,
              'flex min-h-11 min-w-0 max-w-full flex-[0_1_auto] flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5',
            )}
            data-testid="twin-hud-identity"
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span
                className="truncate font-display text-sm font-semibold text-ink"
                data-testid="twin-hud-title"
                title={name?.title ?? assetId}
              >
                {name?.title ?? assetId}
              </span>
              <span className="shrink-0 whitespace-nowrap">
                {liveCapable ? (
                  <Chip tone="neutral">Monitoring</Chip>
                ) : isModelOnlyElement(element) ? (
                  <Chip tone="neutral">Reference model</Chip>
                ) : (
                  overall && <StatusPill colour={overall} label={colourLabel(overall)} />
                )}
              </span>
              {loadStatus === 'loading' && <Chip tone="neutral">loading</Chip>}
              {loadStatus === 'error' && (
                <span title={loadError ?? ''}>
                  <Chip tone="warning">load failed</Chip>
                </span>
              )}
            </div>
            {!diving && asset?.globeLink && (
              <TextButton
                className={twinActionClass}
                onClick={() => {
                  const link = asset.globeLink!;
                  useCatalogueView.getState().enterLoader(link.network);
                  useUI.getState().select({
                    missionId: link.missionId ?? null,
                    antennaId: link.antennaId ?? null,
                    subsystemId: null,
                  });
                  navigate('/loader');
                }}
              >
                <Globe2 className="h-3.5 w-3.5" aria-hidden />
                Show on globe
              </TextButton>
            )}
            <AssetDescription key={assetId} subtitle={name?.subtitle} note={asset?.modelNote} />
            {/* Back to globe while a dive is in the twin */}
            <DiveReturn />
          </div>

          <div
            className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-1.5"
            data-testid="twin-hud-data"
          >
            <div
              className={cn(
                chrome,
                'flex min-w-0 max-w-full flex-wrap items-center gap-1.5 whitespace-nowrap px-2 py-1.5',
              )}
            >
              <Chip
                tone={referenceOnly ? 'neutral' : sourceTone}
                pulse={!referenceOnly && measured && !feedStale && ageMs !== null}
                className="whitespace-nowrap"
              >
                {referenceOnly ? 'REFERENCE' : sourceLabel}
                {referenceOnly ? (
                  <span className="text-[9px] text-ink-3">no telemetry</span>
                ) : ageMs !== null ? (
                  <Freshness ageMs={ageMs} freshMs={freshMs} className="px-0" />
                ) : (
                  <span className="text-[9px] text-ink-3">
                    {liveSource === 'off' ? 'static' : 'no readings'}
                  </span>
                )}
                {!referenceOnly && feedNote && <span className="text-[9px] text-ink-3">{feedNote}</span>}
              </Chip>
              {!referenceOnly && !liveCapable && (
                <select
                  aria-label="Data source"
                  value={liveSource}
                  onChange={(e) => setLiveSource(e.target.value as LiveSource)}
                  className="w-24 rounded-control border border-hairline bg-inset px-1.5 py-0.5 text-[11px] text-ink"
                >
                  <option value="off">JSON only</option>
                  <option value="sim">Simulate</option>
                  <option value="live" disabled={!liveCapable}>
                    Live feed{liveCapable ? '' : ' (RFIMS sites only)'}
                  </option>
                </select>
              )}
              {!referenceOnly && !liveCapable && liveSource === 'sim' && (
                <select
                  aria-label="Simulation interval"
                  value={simIntervalMs}
                  onChange={(e) => setSimIntervalMs(Number(e.target.value))}
                  className="w-14 rounded-control border border-hairline bg-inset px-1.5 py-0.5 font-mono text-[11px] text-ink"
                >
                  {SIM_INTERVALS.map((ms) => (
                    <option key={ms} value={ms}>
                      {ms / 1000} s
                    </option>
                  ))}
                </select>
              )}
              {!referenceOnly && !liveCapable && <FailureSimMenu className="whitespace-nowrap" />}
              <AlertsPopover />
            </div>
            {!liveCapable && (
              <div className={cn(chrome, group, 'gap-0.5 px-1 py-1')}>
                <IconButton label="System report" onClick={() => openDialog('report')}>
                  <ClipboardList className="h-4 w-4" />
                </IconButton>
                <IconButton label="Health dashboard" onClick={() => openDialog('health')}>
                  <HeartPulse className="h-4 w-4" />
                </IconButton>
                <IconButton label="Maintenance history" onClick={() => openDialog('history')}>
                  <ScrollText className="h-4 w-4" />
                </IconButton>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: the scene toolbar */}
        <div
          className={cn(chrome, 'flex max-w-full flex-wrap items-center gap-x-1 gap-y-1.5 px-2 py-1.5')}
          data-testid="twin-hud-toolbar"
        >
          {isOrbitalAsset(asset) && <SatelliteOrbitControls />}
          {siteProfile(asset) && <SiteControls />}
          {asset?.procedural?.kind === 'facility' && <FacilityControls />}
          <div className={group} role="radiogroup" aria-label="View mode">
            {VIEW_MODES.filter(({ mode }) => !liveCapable || (mode !== 'thermal' && mode !== 'exploded')).map(
              ({ mode, label, key, Icon }) => (
                <TextButton
                  key={mode}
                  role="radio"
                  aria-checked={viewMode === mode}
                  active={viewMode === mode}
                  disabled={
                    mode === 'exploded' &&
                    asset?.subviews?.some((v) => v.id === subviewId && v.interior && !v.explodable)
                  }
                  onClick={() => setViewMode(mode)}
                  className="flex items-center gap-1 whitespace-nowrap px-2 @5xl:px-2.5"
                  title={`${label} (${key})`}
                >
                  <Icon className="hidden h-3.5 w-3.5 @5xl:inline" aria-hidden /> {label}
                  <span className="hidden font-mono text-[9px] text-ink-3 @5xl:inline">{key}</span>
                </TextButton>
              ),
            )}
          </div>
          <span className="mx-0.5 h-5 w-px shrink-0 bg-hairline" aria-hidden />
          <div className={group}>
            <IconButton
              label={labelsVisible ? 'Hide labels (L)' : 'Show labels (L)'}
              active={labelsVisible}
              onClick={() => setLabelsVisible(!labelsVisible)}
            >
              <Tag className="h-4 w-4" />
            </IconButton>
            <IconButton
              label={
                bloom ? 'Glow off (bloom and selection outline)' : 'Glow on (bloom and selection outline)'
              }
              active={bloom}
              onClick={() => setBloom(!bloom)}
            >
              <Sparkles className="h-4 w-4" />
            </IconButton>
            <IconButton label="Snapshot (PNG)" onClick={snapshot}>
              <Camera className="h-4 w-4" />
            </IconButton>
          </div>
          {(viewMode === 'exploded' || explode > 0) && (
            <label className={cn(group, 'gap-2 px-1 text-[10px] text-ink-3')}>
              Explode
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={explode}
                onChange={(e) => setExplode(Number(e.target.value))}
                aria-label="Explode amount"
                className="h-1 w-28 accent-[var(--color-accent)]"
              />
              <span className="w-8 font-mono text-ink-2">{Math.round(explode * 100)}%</span>
            </label>
          )}
          {subviews.length > 0 && (
            <>
              <span className="mx-0.5 h-5 w-px shrink-0 bg-hairline" aria-hidden />
              <div className={group}>
                <TextButton
                  active={subviewId === null}
                  onClick={() => setSubview(null)}
                  className="whitespace-nowrap px-2 @5xl:px-2.5"
                >
                  {asset?.id === 'rf_antenna' ? 'Site' : 'Main'}
                </TextButton>
                {subviews.map((sv) => (
                  <TextButton
                    key={sv.id}
                    active={subviewId === sv.id}
                    onClick={() => {
                      setSubview(subviewId === sv.id ? null : sv.id);
                      if (asset?.id === 'rf_antenna' && sv.id === 'container' && subviewId !== sv.id)
                        useUI.getState().select({
                          subsystemId: activeSignalPathId === 'transmit' ? 'transmit_rack' : 'receiver_rack',
                        });
                    }}
                    className="whitespace-nowrap px-2 @5xl:px-2.5"
                  >
                    {sv.label}
                  </TextButton>
                ))}
              </div>
            </>
          )}
          {asset?.id === 'rf_antenna' && (!subviewId || subviewId === 'container') && (
            <select
              aria-label="Camera view"
              className="rounded-control border border-hairline bg-inset px-2 py-1 text-xs text-ink-2"
              value=""
              onChange={(e) => {
                const id = e.target.value;
                if (id === 'main' || id === 'container') useTwin.getState().requestCamera({ poseId: id });
                else {
                  useUI.getState().select({ subsystemId: id });
                  useTwin.getState().requestCamera({ subsystemId: id });
                }
              }}
            >
              <option value="" disabled>
                Camera views
              </option>
              {subviewId === 'container' ? (
                <>
                  <option value="container">Both racks</option>
                  <option value="receiver_rack">Receive rack</option>
                  <option value="transmit_rack">Transmit rack</option>
                </>
              ) : (
                <>
                  <option value="main">Full site</option>
                  <option value="main_antenna">Dish assembly</option>
                  <option value="container_building">Equipment container</option>
                  <option value="site_cabling">Cable routes</option>
                </>
              )}
            </select>
          )}
          {asset?.id === 'ground_station' && <GroundStationControls />}
          {signalPaths.length > 0 && (
            <>
              <span className="mx-0.5 h-5 w-px shrink-0 bg-hairline" aria-hidden />
              <div className={group}>
                <Radio className="h-3.5 w-3.5 text-ink-3" aria-label="Signal path" />
                {signalPaths.map((p) => (
                  <TextButton
                    key={p.id}
                    active={activeSignalPathId === p.id}
                    onClick={() => {
                      setSignalPath(activeSignalPathId === p.id ? null : p.id);
                      if (asset?.id === 'rf_antenna' && subviewId === 'container')
                        useUI.getState().select({
                          subsystemId: p.direction === 'transmit' ? 'transmit_rack' : 'receiver_rack',
                        });
                    }}
                    title={`${p.direction} chain`}
                    aria-label={p.label}
                    className="whitespace-nowrap px-2 @5xl:px-2.5"
                  >
                    <span className="hidden @5xl:inline">{p.label}</span>
                    <span className="@5xl:hidden">{shortPathLabel(p.label)}</span>
                  </TextButton>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom: the event log, and the procedure strip beside it while a procedure runs. On tablets the detail panel is a
          sheet over the bottom of the stage; the stage column publishes its height as --sheet-h (0 without a sheet). */}
      <div
        className="absolute inset-x-4 bottom-[calc(1rem+var(--sheet-h,0px))] flex items-end gap-2"
        data-testid="twin-hud-bottom"
      >
        <EventLog className="w-[min(360px,40%)] min-w-[220px] shrink-0" />
        {procedure && <ProcedureStrip />}
      </div>
    </div>
  );
}

function ProcedureStrip() {
  const state = useTwin((s) => s.procedure)!;
  const setStep = useTwin((s) => s.setProcedureStep);
  const setPlaying = useTwin((s) => s.setProcedurePlaying);
  const stop = useTwin((s) => s.stopProcedure);
  const setTab = useTwinUi((s) => s.setTab);
  const { procedure, stepIndex, playing } = state;
  const step = procedure.steps[stepIndex];
  const total = procedure.steps.length;
  const title = step?.title ?? `Step ${stepIndex + 1}`;
  return (
    <div
      className={cn(chrome, 'mx-auto flex min-w-0 max-w-[640px] flex-1 items-center gap-2 px-3 py-2')}
      data-testid="twin-procedure-strip"
    >
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => setTab('procedures')}
          className="block w-full truncate text-left text-[10px] uppercase tracking-wider text-ink-3 hover:text-ink"
          title="Open the procedure panel"
        >
          {procedure.name}
        </button>
        <div className="truncate text-ink">
          <span className="font-mono text-accent">
            {stepIndex + 1}/{total}
          </span>{' '}
          {title}
        </div>
        <div className="mt-1 h-0.5 w-full overflow-hidden rounded-full bg-inset">
          <div
            className="h-full bg-accent transition-[width] duration-300"
            style={{ width: `${((stepIndex + 1) / total) * 100}%` }}
          />
        </div>
      </div>
      <IconButton label="Previous step" disabled={stepIndex === 0} onClick={() => setStep(stepIndex - 1)}>
        <SkipBack className="h-4 w-4" />
      </IconButton>
      <IconButton label={playing ? 'Pause' : 'Play'} active={playing} onClick={() => setPlaying(!playing)}>
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </IconButton>
      <IconButton label="Next step" disabled={stepIndex >= total - 1} onClick={() => setStep(stepIndex + 1)}>
        <SkipForward className="h-4 w-4" />
      </IconButton>
      <IconButton label="Stop (Esc)" onClick={stop}>
        <Square className="h-4 w-4" />
      </IconButton>
    </div>
  );
}
